import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { ErrorBoundary, ErrorFallback } from "@bi/shared";

// Design tokens load in tiers, in order (see globals.css's header):
// 1. Generated primitives + typography — MUST load first so the semantic aliases resolve.
import "@bi/shared/tokens/variables.css";
// 2. Semantic --rc-color-* aliases (defined against the primitive tier above).
import "@bi/shared/tokens.css";
// 3. App-level globals, styled only on the shared --rc-* tokens.
import "./globals.css";

import { App } from "./App";
import { installQueryCachePersistence } from "./lib/cache-persist";
import { queryClient } from "./lib/query-client";
import { restoreReturnTo } from "./lib/return-to";

// If we just came back from Google, put the address bar back on the deep
// link the reader was signing in for (lib/return-to.ts). BEFORE `createRoot`,
// so the router reads the restored URL when it mounts and the catalog never
// flashes first.
restoreReturnTo();

// Rehydrate the query cache from localStorage BEFORE `createRoot`, so the
// catalog, the dashboard spec, and every still-fresh chart result are already
// in the client when `Boot()` first reads it — a reopen paints the last-known
// board instead of a full-page "Signing in…" and a grid of skeletons, and then
// revalidates behind it. Bounded to 3 hours, matching the backend's own
// `RESULT_TTL_SECONDS` (see lib/cache-persist.ts).
installQueryCachePersistence(queryClient);

const root = document.getElementById("root");
if (!root) throw new Error("missing #root element");

createRoot(root).render(
  <StrictMode>
    <ErrorBoundary
      fallbackRender={() => (
        <ErrorFallback
          variant="page"
          title="Something went wrong"
          message="Reload the page to try again."
          primaryAction={{ label: "Reload page", onClick: () => window.location.reload() }}
        />
      )}
    >
      <App />
    </ErrorBoundary>
  </StrictMode>,
);
